import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/glossyApi";
import { PenLine, Eye, EyeOff } from "lucide-react";

export default function StudioLogin() {
  const { login, logout, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "The Studio — S/ME";
    if (!authLoading && user) {
      if (user.role === "admin") navigate("/admin", { replace: true });
      else navigate("/portal", { replace: true });
    }
  }, [user, authLoading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(form.email.trim(), form.password.trim());
      if (u.role !== "admin") {
        logout();
        setError("This entrance is for the editorial desk only. Members sign in from the magazine's Sign in page.");
        setLoading(false);
        return;
      }
      navigate("/admin");
    } catch (err) {
      const status = err.response?.status;
      if (!err.response || status >= 500) {
        setError("The Studio server didn't respond just then — give it a few seconds and press Enter the Studio again.");
      } else {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-cream grain flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-10">
          <span className="font-serif text-4xl">S/ME</span>
          <span className="overline text-gloss-gold border-l border-cream/20 pl-3">The Studio</span>
        </div>
        <p className="overline text-cream/40 mb-3">Editorial desk only</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-8">Editor sign in</h1>

        {error && <div className="bg-red-500/10 border border-red-500 text-red-400 px-4 py-3 font-sans text-sm mb-6" data-testid="studio-auth-error">{error}</div>}

        <form onSubmit={submit} className="space-y-6" data-testid="studio-auth-form">
          <label className="block">
            <span className="overline text-cream/40">Email</span>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email"
              className="mt-2 w-full bg-transparent border-b border-cream/30 py-3 font-sans text-cream focus:outline-none focus:border-gloss-gold transition-colors" data-testid="studio-email-input" />
          </label>
          <label className="block">
            <span className="overline text-cream/40">Password</span>
            <div className="relative">
              <input type={showPass ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required autoComplete="current-password"
                className="mt-2 w-full bg-transparent border-b border-cream/30 py-3 pr-10 font-sans text-cream focus:outline-none focus:border-gloss-gold transition-colors" data-testid="studio-password-input" />
              <button type="button" onClick={() => setShowPass(!showPass)} tabIndex={-1} aria-label={showPass ? "Hide password" : "Show password"}
                className="absolute right-0 bottom-3 text-cream/40 hover:text-cream transition-colors" data-testid="studio-password-toggle">
                {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </label>
          <button type="submit" disabled={loading} className="w-full bg-cream text-ink py-4 overline flex items-center justify-center gap-2 hover:bg-gloss-gold transition-colors disabled:opacity-60" data-testid="studio-submit">
            <PenLine size={14} /> {loading ? "Opening the Studio…" : "Enter the Studio"}
          </button>
        </form>

        <p className="font-mono text-[10px] text-cream/30 mt-10">Not an editor? <Link to="/" className="underline hover:text-cream/60">Back to the magazine</Link></p>
      </div>
    </div>
  );
}
